import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, map, Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';

import {
  // User
  BankingUserModel,
  BankingUserCreationRequestModel,
  // Bank
  BankingBankModel,
  BankingBankRequestModel,
  BankingBankResponseModel,
  BankingDashboardDataModel,
  // Account
  BankingAccountModel,
  BankingAccountsResponseModel,
  BankingAccountThresholdsUpdateRequestModel,
  BankingUpdateVisibilityRequestModel,
  // Transaction
  BankingTransactionModel,
  BankingTransactionRequestModel,
  BankingTransactionsResponseModel,
  BankingLatestTransactionsModel,
  BankingTransactionAttachmentModel,
  // Tag
  BankingTagModel,
  BankingTagCreateUpdateModel,
  // Category
  BankingCategoryModel,
  // Contract
  BankingContractsResponseModel,
  // Analysis
  CashflowAnalysisDto,
  ClientEventDto,
  ClientEventStatus,
  ClientOpenEventsDto,
  BankingAnalysisModel,
  BankingAnalysisCreateRequestModel,
  // Analysis Report
  BankingAnalysisReportRequestModel,
  BankingAnalysisReportResponseModel,
  // Securities
  BankingDepotDashboardResponseModel,
  // Session
  BankingBankLoginsResponseModel,
  BankingSecurityModel,
} from '../../model';

@Injectable({
  providedIn: 'root',
})
export class BankingService {
  constructor(private http: HttpClient) {}

  // ===========================================================================
  // Session Resource (B2B)
  // ===========================================================================

  /** GET /banking/securities/{accountId} — Get all securities for an account */
  public getSecuritiesByAccountId(clientId: string, accountId: string): Observable<BankingSecurityModel[]> {
    return this.http.get<BankingSecurityModel[]>(
      `${environment.apiEndpoint}/banking/securities/${clientId}/${accountId}`,
    );
  }

  /** GET /banking/session/logins — Get bank login statuses (always fetches fresh from FinoOS) */
  public getBankLoginStatus(clientId: string): Observable<BankingBankLoginsResponseModel> {
    const params = new HttpParams().set('client_id', clientId);
    return this.http.get<BankingBankLoginsResponseModel>(`${environment.apiEndpoint}/banking/session/logins`, {
      params,
    });
  }

  // ===========================================================================
  // User Resource (B2B)
  // ===========================================================================

  /** GET /banking/users — Get all active users */
  public getAllUsers(): Observable<BankingUserModel[]> {
    return this.http.get<BankingUserModel[]>(`${environment.apiEndpoint}/banking/users`);
  }

  /** GET /banking/users/{clientId} — Get user by client ID */
  public getUserByClientId(clientId: string): Observable<BankingUserModel> {
    return this.http.get<BankingUserModel>(`${environment.apiEndpoint}/banking/users/${clientId}`);
  }

  /** GET /banking/users/{clientId}/exists — Check if a user exists (200 = exists, 404 = not found) */
  public checkUserExists(clientId: string): Observable<void> {
    return this.http.get<void>(`${environment.apiEndpoint}/banking/users/${clientId}/exists`);
  }

  /** POST /banking/users — Create a new user in FinoOS */
  public createUser(body: BankingUserCreationRequestModel): Observable<BankingUserModel> {
    return this.http.post<BankingUserModel>(`${environment.apiEndpoint}/banking/users`, body);
  }

  /** POST /banking/users/{clientId}/sync — Sync user data from FinoOS */
  public syncUser(clientId: string): Observable<BankingUserModel> {
    return this.http.post<BankingUserModel>(`${environment.apiEndpoint}/banking/users/${clientId}/sync`, null);
  }

  /** DELETE /banking/users/{clientId} — Delete user from FinoOS and deactivate locally */
  public deleteUser(clientId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiEndpoint}/banking/users/${clientId}`);
  }

  // ===========================================================================
  // Bank Resource (B2B)
  // ===========================================================================

  /** GET /banking/banks — Get all banks for a user */
  public getAllBanks(request: BankingBankRequestModel): Observable<BankingBankResponseModel> {
    let params = new HttpParams().set('client_id', request.client_id);
    if (request.withAccounts) {
      params = params.set('withAccounts', 'true');
    }
    if (request.types?.length) {
      request.types.forEach((type) => {
        params = params.append('types', type);
      });
    }
    return this.http.get<BankingBankResponseModel>(`${environment.apiEndpoint}/banking/banks/sharable`, { params });
  }

  /** GET /banking/banks/{bankId} — Get a specific bank */
  public getBankById(bankId: string, request: BankingBankRequestModel): Observable<BankingBankModel> {
    let params = new HttpParams().set('client_id', request.client_id);
    if (request.withAccounts) {
      params = params.set('withAccounts', 'true');
    }
    return this.http.get<BankingBankModel>(`${environment.apiEndpoint}/banking/banks/${bankId}`, { params });
  }

  /** DELETE /banking/banks/{bankId} — Delete a bank and all associated data */
  public deleteBank(bankId: string, clientId: string): Observable<void> {
    const params = new HttpParams().set('client_id', clientId);
    return this.http.delete<void>(`${environment.apiEndpoint}/banking/banks/${bankId}`, { params });
  }

  /** GET /banking/banks/dashboard — Get banking dashboard (balance, banks, latest transactions) */
  public getDashboard(clientId: string, visibleOnly?: boolean): Observable<BankingDashboardDataModel> {
    let params = new HttpParams().set('client_id', clientId);
    if (visibleOnly !== undefined) {
      params = params.set('visibleOnly', visibleOnly);
    }
    return this.http.get<BankingDashboardDataModel>(`${environment.apiEndpoint}/banking/banks/dashboard`, {
      params,
    });
  }

  /** PATCH /banking/banks/reorder — Reorder banks by supplying IDs in desired order */
  public reorderBanks(clientId: string, bankIds: string[]): Observable<BankingBankResponseModel> {
    const params = new HttpParams().set('client_id', clientId);
    return this.http.patch<BankingBankResponseModel>(`${environment.apiEndpoint}/banking/banks/reorder`, bankIds, {
      params,
    });
  }

  // ===========================================================================
  // Account Resource (B2B)
  // ===========================================================================

  /** GET /banking/accounts/{clientId} — Get all accounts for a user */
  public getAccountsByClientId(clientId: string): Observable<BankingAccountsResponseModel> {
    return this.http.get<BankingAccountsResponseModel>(`${environment.apiEndpoint}/banking/accounts/${clientId}`);
  }

  /** GET /banking/accounts/{clientId}/{accountId} — Get a single account */
  public getAccountById(clientId: string, accountId: string): Observable<BankingAccountModel> {
    return this.http.get<BankingAccountModel>(`${environment.apiEndpoint}/banking/accounts/${clientId}/${accountId}`);
  }

  /** POST /banking/accounts/{clientId}/sync — Sync accounts from FinoOS */
  public syncAccounts(clientId: string): Observable<BankingAccountsResponseModel> {
    return this.http.post<BankingAccountsResponseModel>(
      `${environment.apiEndpoint}/banking/accounts/${clientId}/sync`,
      null,
    );
  }

  /** PATCH /banking/accounts/{clientId}/{accountId}/toggle-sharable — Toggle sharable status */
  public toggleAccountSharable(clientId: string, accountId: string): Observable<BankingAccountModel> {
    return this.http.patch<BankingAccountModel>(
      `${environment.apiEndpoint}/banking/accounts/${clientId}/${accountId}/toggle-sharable`,
      null,
    );
  }

  /** PATCH /banking/accounts/{clientId}/{accountId}/toggle-censored-push — Toggle censored push notification */
  public toggleAccountCensoredPush(clientId: string, accountId: string): Observable<BankingAccountModel> {
    return this.http.patch<BankingAccountModel>(
      `${environment.apiEndpoint}/banking/accounts/${clientId}/${accountId}/toggle-censored-push`,
      null,
    );
  }

  /** PATCH /banking/accounts/{clientId}/{accountId}/thresholds — Update balance and transaction warning thresholds */
  public updateAccountThresholds(
    clientId: string,
    accountId: string,
    body: BankingAccountThresholdsUpdateRequestModel,
  ): Observable<BankingAccountModel> {
    return this.http.patch<BankingAccountModel>(
      `${environment.apiEndpoint}/banking/accounts/${clientId}/${accountId}/thresholds`,
      body,
    );
  }

  /** PATCH /banking/accounts/{clientId}/{accountId}/name — Update account display name */
  public updateAccountName(clientId: string, accountId: string, name: string): Observable<BankingAccountModel> {
    return this.http.patch<BankingAccountModel>(
      `${environment.apiEndpoint}/banking/accounts/${clientId}/${accountId}/name`,
      name,
    );
  }

  /** PATCH /banking/accounts/{clientId}/visibility — Update visibility for multiple accounts */
  public updateAccountVisibility(
    clientId: string,
    body: BankingUpdateVisibilityRequestModel[],
  ): Observable<BankingAccountsResponseModel> {
    return this.http.patch<BankingAccountsResponseModel>(
      `${environment.apiEndpoint}/banking/accounts/${clientId}/visibility`,
      body,
    );
  }

  /** PATCH /banking/accounts/{clientId}/reorder — Reorder accounts by supplying IDs in desired order */
  public reorderAccounts(clientId: string, accountIds: string[]): Observable<BankingAccountsResponseModel> {
    return this.http.patch<BankingAccountsResponseModel>(
      `${environment.apiEndpoint}/banking/accounts/${clientId}/reorder`,
      accountIds,
    );
  }

  // ===========================================================================
  // Transaction Resource (B2B)
  // ===========================================================================

  /** GET /banking/transactions/{clientId}/accounts — Paginated transactions with rich filtering */
  public getTransactions(
    clientId: string,
    request: BankingTransactionRequestModel,
  ): Observable<BankingTransactionsResponseModel> {
    let params = new HttpParams();
    Object.keys(request).forEach((key) => {
      const value = (request as any)[key];
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach((v) => {
            params = params.append(key, v);
          });
        } else {
          params = params.set(key, value);
        }
      }
    });
    return this.http.get<BankingTransactionsResponseModel>(
      `${environment.apiEndpoint}/banking/transactions/${clientId}/accounts`,
      { params },
    );
  }

  /** GET /banking/transactions/{clientId}/latest — Latest transactions from visible accounts (paginated) */
  public getLatestTransactions(
    clientId: string,
    page: number = 0,
    size: number = 50,
  ): Observable<BankingLatestTransactionsModel> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http.get<BankingLatestTransactionsModel>(
      `${environment.apiEndpoint}/banking/transactions/${clientId}/latest`,
      { params },
    );
  }

  /** GET /banking/transactions/{clientId}/{transactionId} — Get a single transaction */
  public getTransactionById(clientId: string, transactionId: string): Observable<BankingTransactionModel> {
    return this.http.get<BankingTransactionModel>(
      `${environment.apiEndpoint}/banking/transactions/${clientId}/${transactionId}`,
    );
  }

  /** POST /banking/transactions/{clientId}/sync — Sync all transactions from FinoOS */
  public syncTransactions(clientId: string): Observable<BankingTransactionsResponseModel> {
    return this.http.post<BankingTransactionsResponseModel>(
      `${environment.apiEndpoint}/banking/transactions/${clientId}/sync`,
      null,
    );
  }

  /** PUT /banking/transactions/{clientId}/{transactionId}/exclude-from-analysis — Set exclude from analysis flag */
  public excludeTransactionFromAnalysis(
    clientId: string,
    transactionId: string,
    exclude: boolean,
  ): Observable<BankingTransactionModel> {
    return this.http.put<BankingTransactionModel>(
      `${environment.apiEndpoint}/banking/transactions/${clientId}/${transactionId}/exclude-from-analysis`,
      exclude,
    );
  }

  /** PUT /banking/transactions/{clientId}/{transactionId}/main-category — Update main category */
  public updateTransactionMainCategory(
    clientId: string,
    transactionId: string,
    categoryId: number,
  ): Observable<BankingTransactionModel> {
    return this.http.put<BankingTransactionModel>(
      `${environment.apiEndpoint}/banking/transactions/${clientId}/${transactionId}/main-category`,
      categoryId,
    );
  }

  /** GET /banking/transactions/{clientId}/{transactionId}/attachments — Get all attachments for a transaction */
  public getTransactionAttachments(
    clientId: string,
    transactionId: string,
  ): Observable<BankingTransactionAttachmentModel[]> {
    return this.http.get<BankingTransactionAttachmentModel[]>(
      `${environment.apiEndpoint}/banking/transactions/${clientId}/${transactionId}/attachments`,
    );
  }

  /** DELETE /banking/transactions/{clientId}/{transactionId}/attachments/{attachmentId} — Delete an attachment */
  public deleteTransactionAttachment(clientId: string, transactionId: string, attachmentId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiEndpoint}/banking/transactions/${clientId}/${transactionId}/attachments/${attachmentId}`,
    );
  }

  // ===========================================================================
  // Tag Resource (B2B)
  // ===========================================================================

  /** GET /banking/tags/{clientId} — Get all tags alphabetically */
  public getAllTags(clientId: string): Observable<BankingTagModel[]> {
    return this.http.get<BankingTagModel[]>(`${environment.apiEndpoint}/banking/tags/${clientId}`);
  }

  /** GET /banking/tags/{clientId}/{tagId} — Get a single tag name */
  public getTagById(clientId: string, tagId: number): Observable<string> {
    return this.http.get<string>(`${environment.apiEndpoint}/banking/tags/${clientId}/${tagId}`);
  }

  /** POST /banking/tags/{clientId} — Create a new tag (name must be unique per client) */
  public createTag(clientId: string, body: BankingTagCreateUpdateModel): Observable<BankingTagModel> {
    return this.http.post<BankingTagModel>(`${environment.apiEndpoint}/banking/tags/${clientId}`, body);
  }

  /** PUT /banking/tags/{clientId}/{tagId} — Update an existing tag */
  public updateTag(clientId: string, tagId: number, body: BankingTagCreateUpdateModel): Observable<BankingTagModel> {
    return this.http.put<BankingTagModel>(`${environment.apiEndpoint}/banking/tags/${clientId}/${tagId}`, body);
  }

  /** DELETE /banking/tags/{clientId}/{tagId} — Delete a tag and remove it from all transactions */
  public deleteTag(clientId: string, tagId: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiEndpoint}/banking/tags/${clientId}/${tagId}`);
  }

  /** PUT /banking/transactions/{clientId}/{transactionId}/tags — Replace ALL tags on a transaction */
  public setTransactionTags(
    clientId: string,
    transactionId: string,
    tagIds: number[],
  ): Observable<BankingTransactionModel> {
    return this.http.put<BankingTransactionModel>(
      `${environment.apiEndpoint}/banking/transactions/${clientId}/${transactionId}/tags`,
      tagIds,
    );
  }

  /** POST /banking/transactions/{clientId}/{transactionId}/tags — Add tags while keeping existing ones */
  public addTransactionTags(
    clientId: string,
    transactionId: string,
    tagIds: number[],
  ): Observable<BankingTransactionModel> {
    return this.http.post<BankingTransactionModel>(
      `${environment.apiEndpoint}/banking/transactions/${clientId}/${transactionId}/tags`,
      tagIds,
    );
  }

  /** DELETE /banking/transactions/{clientId}/{transactionId}/tags — Remove specific tags from a transaction */
  public removeTransactionTags(clientId: string, transactionId: string, tagIds: number[]): Observable<void> {
    const params = tagIds.length ? new HttpParams().set('tagIds', tagIds.join(',')) : new HttpParams();
    return this.http.delete<void>(`${environment.apiEndpoint}/banking/transactions/${clientId}/${transactionId}/tags`, {
      params,
    });
  }

  // ===========================================================================
  // Category Resource (B2B)
  // ===========================================================================

  /** GET /banking/categories — Get all main FinoOS categories (cached indefinitely) */
  public getCategories(): Observable<BankingCategoryModel[]> {
    return this.http.get<BankingCategoryModel[]>(`${environment.apiEndpoint}/banking/categories`);
  }

  // ===========================================================================
  // Contract Resource (B2B — service-to-service by clientId)
  // ===========================================================================

  /** GET /banking/contracts/{clientId} — Get contracts for a client (service-to-service) */
  public getContractsByClientId(clientId: string, excludedIds?: string[]): Observable<BankingContractsResponseModel> {
    let params = new HttpParams();
    if (excludedIds?.length) {
      excludedIds.forEach((id) => {
        params = params.append('excludedIds', id);
      });
    }
    return this.http.get<BankingContractsResponseModel>(`${environment.apiEndpoint}/banking/contracts/${clientId}`, {
      params,
    });
  }

  // ===========================================================================
  // Analysis Resource (B2B)
  // ===========================================================================

  /**
   * GET /banking/cashflow/{clientId} — Cashflow analysis over ALL active accounts of the client (sharable flag
   * ignored), last `months` full calendar months ending with `until` (yyyy-MM, default: previous month).
   */
  public getCashflowAnalysis(clientId: string, months: number = 12, until?: string): Observable<CashflowAnalysisDto> {
    let params: HttpParams = new HttpParams().set('months', months);
    if (until) {
      params = params.set('until', until);
    }
    return this.http.get<CashflowAnalysisDto>(`${environment.apiEndpoint}/banking/cashflow/${clientId}`, { params });
  }

  /**
   * PUT /banking/cashflow/{clientId}/accounts/{accountId}/included — include or leave out an account in the cashflow
   * analysis (KPIs, AI scan, events, overview). Left-out accounts stay in the accounts list with includedInCashflow false.
   */
  public setCashflowAccountIncluded(
    clientId: string,
    accountId: string,
    included: boolean,
  ): Observable<BankingAccountModel> {
    return this.http.put<BankingAccountModel>(
      `${environment.apiEndpoint}/banking/cashflow/${clientId}/accounts/${accountId}/included`,
      { included },
    );
  }

  /** GET /banking/cashflow/{clientId}/events — events detected on the client's bank data, newest first */
  public getClientEvents(clientId: string): Observable<ClientEventDto[]> {
    return this.http.get<ClientEventDto[]>(`${environment.apiEndpoint}/banking/cashflow/${clientId}/events`);
  }

  /** POST /banking/cashflow/{clientId}/events/detect — run the rules now, returns the newly created events */
  public detectClientEvents(clientId: string): Observable<ClientEventDto[]> {
    return this.http.post<ClientEventDto[]>(
      `${environment.apiEndpoint}/banking/cashflow/${clientId}/events/detect`,
      {},
    );
  }

  /** GET /banking/cashflow/events/open-clients — all clients with open (NEW/SEEN) bank events, newest first */
  public getClientsWithOpenEvents(): Observable<ClientOpenEventsDto[]> {
    return this.http.get<ClientOpenEventsDto[]>(`${environment.apiEndpoint}/banking/cashflow/events/open-clients`);
  }

  /** PATCH /banking/cashflow/{clientId}/events/{eventId}/status */
  public updateClientEventStatus(
    clientId: string,
    eventId: string,
    status: ClientEventStatus,
  ): Observable<ClientEventDto> {
    return this.http.patch<ClientEventDto>(
      `${environment.apiEndpoint}/banking/cashflow/${clientId}/events/${eventId}/status`,
      { status },
    );
  }

  /** GET /banking/analysis/{clientId}/{analysisId} — Get a specific analysis */
  public getAnalysis(clientId: string, analysisId: string): Observable<BankingAnalysisModel> {
    return this.http.get<BankingAnalysisModel>(`${environment.apiEndpoint}/banking/analysis/${clientId}/${analysisId}`);
  }

  /** POST /banking/analysis/{clientId} — Create a named analysis with selected accounts */
  public createAnalysis(clientId: string, body: BankingAnalysisCreateRequestModel): Observable<BankingAnalysisModel> {
    return this.http.post<BankingAnalysisModel>(`${environment.apiEndpoint}/banking/analysis/${clientId}`, body);
  }

  /** PUT /banking/analysis/{clientId}/{analysisId} — Update an existing analysis */
  public updateAnalysis(
    clientId: string,
    analysisId: string,
    body: BankingAnalysisCreateRequestModel,
  ): Observable<BankingAnalysisModel> {
    return this.http.put<BankingAnalysisModel>(
      `${environment.apiEndpoint}/banking/analysis/${clientId}/${analysisId}`,
      body,
    );
  }

  /** DELETE /banking/analysis/{clientId}/{analysisId} — Delete a saved analysis */
  public deleteAnalysis(clientId: string, analysisId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiEndpoint}/banking/analysis/${clientId}/${analysisId}`);
  }

  /**
   * Lists every analysis the advisor may open for a client: the ones the advisor created himself
   * (GET /banking/analysis/{clientId}, advisor-scoped on the backend) plus the client's own analyses
   * whose accounts are all shared (GET /banking/analysis/{clientId}/shared). Both lists are merged,
   * de-duplicated by id and sorted newest first.
   */
  public listAnalyses(clientId: string): Observable<BankingAnalysisModel[]> {
    return forkJoin([
      this.http.get<BankingAnalysisModel[]>(`${environment.apiEndpoint}/banking/analysis/${clientId}`),
      this.http.get<BankingAnalysisModel[]>(`${environment.apiEndpoint}/banking/analysis/${clientId}/shared`),
    ]).pipe(
      map(([own, shared]: [BankingAnalysisModel[], BankingAnalysisModel[]]): BankingAnalysisModel[] => {
        const byId = new Map<string, BankingAnalysisModel>();
        [...(own ?? []), ...(shared ?? [])].forEach((analysis: BankingAnalysisModel): void => {
          byId.set(analysis.id, analysis);
        });
        return [...byId.values()].sort((a: BankingAnalysisModel, b: BankingAnalysisModel): number =>
          (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
        );
      }),
    );
  }

  // ===========================================================================
  // Analysis Report Resource (B2B)
  // ===========================================================================

  /** POST /banking/analysis/{clientId}/report — Generate end customer analysis report */
  public generateReport(
    clientId: string,
    body: BankingAnalysisReportRequestModel,
  ): Observable<BankingAnalysisReportResponseModel> {
    return this.http.post<BankingAnalysisReportResponseModel>(
      `${environment.apiEndpoint}/banking/analysis-report/${clientId}/report`,
      body,
    );
  }

  // ===========================================================================
  // Securities Resource (B2B)
  // ===========================================================================

  /** GET /banking/securities/dashboard — Get securities depot dashboard */
  public getDepotDashboard(clientId: string, visibleOnly?: boolean): Observable<BankingDepotDashboardResponseModel> {
    let params = new HttpParams().set('client_id', clientId);
    if (visibleOnly !== undefined) {
      params = params.set('visibleOnly', visibleOnly);
    }
    return this.http.get<BankingDepotDashboardResponseModel>(
      `${environment.apiEndpoint}/banking/securities/dashboard`,
      { params },
    );
  }
}
